import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Button, Stack, Typography } from '@mui/material';
import {
  GridRowId,
  GridRowModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { format, parseISO, startOfToday } from 'date-fns';
import { useState } from 'react';

import useAuth from 'portal/src/auth/useAuth';
import ConfirmDialog from 'portal/src/components/common/ConfirmDialog';
import DataTable from 'portal/src/components/common/DataTable';
import GenericDialog from 'portal/src/components/common/GenericDialog';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';
import { dateOnlyFormat } from 'portal/src/validators/dateValidator';

import HistoricalValueForm from './HistoricalValueForm';
import {
  getColumns,
  getFields,
  IHistoricalValueFormData,
} from './HistoricalValues.fields';
import { UseHistoricalValuesReturn } from './useHistoricalValues';

interface HistoricalValuesDialogTableProps<T> {
  historyConfigs: UseHistoricalValuesReturn<T>;
  targetColumnType: 'number' | 'string';
  targetHeaderName: string;
  allowDecimal?: boolean;
}

function HistoricalValuesDialogTable<T>({
  historyConfigs: {
    data,
    open,
    handleClose,
    handleSave,
    isDirty,
    errorStr,
    handleDataChange,
  },
  targetColumnType,
  targetHeaderName,
  allowDecimal = false,
}: HistoricalValuesDialogTableProps<T>) {
  const { hasReadOnlyAccess } = useAuth();
  const snackbarHandler = useSnackbarHandler();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showHistoricalValueForm, setShowHistoricalValueForm] = useState(false);
  const [deleteCandidateId, setDeleteCandidateId] = useState<GridRowId | null>(
    null,
  );

  const lowerCasedHeaderName = targetHeaderName.toLowerCase();
  // get the largest id value to use as a staring point for new row indexes to avoid row id collisions
  let maxId = Math.max(...data.map((i) => i.id).filter((i) => !!i)) + 1;
  if (!Number.isSafeInteger(maxId)) {
    maxId = 0;
  }
  const rows = data.map((item, index) => ({
    ...item,
    id: item.id ?? maxId + index, // New rows will not have an ID yet
  }));
  const lastRow = rows.at(-1);
  const maxDate = lastRow?.startDate
    ? parseISO(lastRow.startDate)
    : startOfToday();

  const processRowUpdate = (newRow: GridRowModel) => {
    if (newRow.id === lastRow?.id) {
      return newRow;
    }

    const updatedRows = rows.map((row) =>
      row.id === newRow.id
        ? {
            ...row,
            endDate: newRow.endDate,
            startDate: newRow.startDate,
            value: newRow.value,
          }
        : row,
    );

    handleDataChange(updatedRows);

    return newRow;
  };

  const handleRowSelectionChange = (model: GridRowSelectionModel) => {
    const selectedIds = Array.from(model.ids);
    setDeleteCandidateId(selectedIds[0] ?? null);
  };

  const handleRemoveHistoricalValue = () => {
    if (deleteCandidateId === null) {
      return;
    }

    handleDataChange(rows.filter((row) => row.id !== deleteCandidateId));

    setDeleteCandidateId(null);
    setShowDeleteDialog(false);
  };

  const handleCloseHistoricalValueForm = () => {
    setShowHistoricalValueForm(false);
    setDeleteCandidateId(null);
  };

  const handleSaveHistoricalValue = (payload: IHistoricalValueFormData<T>) => {
    handleDataChange([
      ...data,
      {
        value: payload.value,
        startDate: payload.startDate
          ? format(payload.startDate, dateOnlyFormat)
          : null,
        endDate: payload.endDate
          ? format(payload.endDate, dateOnlyFormat)
          : null,
      },
    ]);
    handleCloseHistoricalValueForm();
  };

  const resetAndCloseModal = () => {
    handleClose();
    setShowDeleteDialog(false);
    setShowHistoricalValueForm(false);
    setDeleteCandidateId(null);
  };

  const saveAndExit = () => {
    handleSave(data);
    snackbarHandler.success('Historical values ready to be saved.');
  };

  const gridColumns = getColumns(
    targetColumnType,
    targetHeaderName,
    allowDecimal,
    maxDate,
  );

  const formFieldsConfig = getFields(
    maxDate,
    targetHeaderName,
    targetColumnType,
    allowDecimal,
  );

  return (
    <>
      <GenericDialog
        title={`${targetHeaderName} History`}
        open={open}
        onClose={resetAndCloseModal}
        dialogActions={
          <Stack alignItems="flex-end">
            {errorStr && (
              <Typography variant="body2" color="error" marginBottom={1}>
                {errorStr}
              </Typography>
            )}
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={resetAndCloseModal}>
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={hasReadOnlyAccess() || !!errorStr || !isDirty}
                disableElevation
                onClick={saveAndExit}
                variant="contained"
              >
                Save changes
              </Button>
            </Stack>
          </Stack>
        }
      >
        <Typography
          sx={{ display: 'flex', alignItems: 'center' }}
          variant="body2"
          marginBottom={2}
        >
          <KeyboardArrowRightIcon sx={{ width: 15, marginRight: 1 }} />
          Only past entries can be added or edited. To change the last entry in
          this table, edit the '{targetHeaderName}' field directly.
        </Typography>
        <DataTable
          actionsConfig={{
            inRowEditConfig: {
              processRowUpdate,
              btnConfig: (row) => ({
                editHiddenForRow: row.id === lastRow?.id,
                onDeleteClick: () => {
                  setDeleteCandidateId(row.id);
                  setShowDeleteDialog(true);
                },
              }),
            },
          }}
          columns={gridColumns}
          checkboxSelection={false}
          isCellEditable={(params) => params.id !== lastRow?.id}
          isRowNavigation={false}
          onRowSelectionModelChange={handleRowSelectionChange}
          rows={rows}
          toolbarSlotProps={{
            children: (
              <Button
                color="primary"
                disabled={hasReadOnlyAccess()}
                startIcon={<AddIcon />}
                onClick={() => setShowHistoricalValueForm(true)}
                size="small"
                variant="outlined"
              >
                Add Item
              </Button>
            ),
          }}
        />
      </GenericDialog>
      <HistoricalValueForm
        defaultValues={{
          startDate: null,
          endDate: null,
          value: (targetColumnType === 'string' ? '' : null) as T,
        }}
        formFieldsConfig={formFieldsConfig}
        open={showHistoricalValueForm}
        onClose={handleCloseHistoricalValueForm}
        onSave={handleSaveHistoricalValue}
        maxDate={maxDate}
      />
      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        message={`Are you sure you want to delete this historical ${lowerCasedHeaderName} value?`}
        primaryButtonLabel="Yes"
        onPrimaryButtonClicked={handleRemoveHistoricalValue}
        secondaryButtonLabel="No"
        onSecondaryButtonClicked={() => setShowDeleteDialog(false)}
        renderElement={<DeleteIcon sx={{ color: 'error.main' }} />}
      />
    </>
  );
}

export default HistoricalValuesDialogTable;
